import { Download, Share, Smartphone, X } from 'lucide-react';
import { useState } from 'react';
import { useInstall } from '../lib/pwa.ts';
import { Button } from './ui/button.tsx';

const KEY = 'helpdesk.installDismissed';
/** "Not now" hides the banner for this long; Settings still has the button. */
const SNOOZE_MS = 30 * 24 * 60 * 60_000;

function snoozed() {
  try {
    return Date.now() - Number(localStorage.getItem(KEY) ?? 0) < SNOOZE_MS;
  } catch {
    return false;
  }
}

/**
 * Suggests installing the helpdesk as an app, before or after signing in.
 * Chrome, Edge and Android get an Install button; iPhone and iPad have no
 * prompt, so they get the Share → Add to Home Screen steps instead.
 */
export function InstallBanner() {
  const install = useInstall();
  const [hidden, setHidden] = useState(snoozed);

  if (hidden || install.installed || !(install.canPrompt || install.iosManual)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, String(Date.now()));
    } catch {
      // Not persisted in private browsing; it just shows again next time.
    }
    setHidden(true);
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-brand/30 bg-brand-soft/60 p-4">
      <Smartphone className="size-5 shrink-0 text-brand" aria-hidden />
      <div className="min-w-48 flex-1 text-sm">
        <p className="font-medium">Install the helpdesk app</p>
        <p className="text-muted">
          {install.iosManual ? (
            <>
              Tap <Share className="inline size-4 align-text-bottom" aria-label="Share" /> Share, then “Add to
              Home Screen”. It opens like an app and can notify you when IT replies.
            </>
          ) : (
            'Open it from your home screen like any other app, and get notified when IT replies.'
          )}
        </p>
      </div>
      <div className="flex gap-2">
        {install.canPrompt && (
          <Button size="sm" onClick={() => void install.install()}>
            <Download className="size-4" aria-hidden />
            Install
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={dismiss} aria-label="Not now">
          <X className="size-4 sm:hidden" aria-hidden />
          <span className="hidden sm:inline">Not now</span>
        </Button>
      </div>
    </div>
  );
}
